/**
 * Zod Validation Schemas for Block Types
 * Requirements: 2.2, 2.3, 2.4
 */

import { z } from 'zod';

import type { ClickExpandType } from './blocks';
import { CLICK_EXPAND_MIN_TOPICS, getClickExpandMaxTopics } from './clickExpand';

// Video track schema - kind, label, language required; src can be empty during editing
export const VideoTrackSchema = z.object({
  kind: z.enum(['captions', 'subtitles', 'chapters']),
  src: z.string(), // Allow empty during editing, validate on publish
  label: z.string().min(1, 'Track label is required'),
  language: z.string().min(1, 'Track language is required'),
  default: z.boolean().optional(),
});

const SuccessAnimationTypeSchema = z.enum(['confetti', 'pixie-dust', 'gold-coins']);

// Base block schema
const BaseBlockSchema = z.object({
  id: z.string().min(1, 'Block ID is required'),
  requireInteraction: z.boolean().optional(),
  interactionMessage: z.string().optional(),
  successAnimationType: SuccessAnimationTypeSchema.optional(),
  hideResults: z.boolean().optional(),
  locked: z.boolean().optional(),
  /** @deprecated Legacy field-level lock; accepted for load-time migration to `locked` only. */
  lockedFields: z.array(z.string()).optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Client-context customization markers (editor read only). Declared here so
  // the strict block schema preserves them through (de)serialization instead of
  // stripping them as unknown keys.
  renderState: z.enum(['render', 'suppress'] as const).optional(),
  customizationState: z.enum(['inherited', 'override', 'suppressed'] as const).optional(),
  clientAdded: z.boolean().optional(),
  // NA-408: learner-facing translation provenance for the resolved locale.
  translationType: z.enum(['mt', 'ht'] as const).nullable().optional(),
});

// TipTap JSONContent schema - reusable pattern for rich text fields
const TipTapContentSchema = z.object({
  type: z.string(),
  content: z.array(z.any()).optional(),
}).passthrough();

// Video block schema - URL can be empty during editing, at least one caption track required
/** Canonical h1-h4 title-level enum. Per-block names below are aliases kept
 * for API stability — they are all the same schema object. */
export const TitleLevelSchema = z.enum(['h1', 'h2', 'h3', 'h4']);

export const VideoTitleLevelSchema = TitleLevelSchema;

export const VideoBlockSchema = BaseBlockSchema.extend({
  type: z.literal('video'),
  url: z.string().refine(
    (val) => val === '' || z.string().url().safeParse(val).success,
    'Video URL must be a valid URL'
  ),
  posterUrl: z.string().refine(
    (val) => !val || z.string().url().safeParse(val).success,
    'Poster URL must be a valid URL'
  ).optional(),
  tracks: z.array(VideoTrackSchema).min(1, 'At least one caption track is required for accessibility'),
  centerstageVideoId: z.string().optional(),
  title: z.string().optional(),
  titleLevel: VideoTitleLevelSchema.optional(),
  shortDescription: z.string().max(175, 'Short description must be 175 characters or fewer').optional(),
  longDescription: z.union([z.string(), TipTapContentSchema]).optional(),
});

export const BasicContentLayoutSchema = z.enum(['image-top', 'image-right', 'image-left', 'no-image']);
export const BasicContentImageSizeSchema = z.enum(['small', 'medium', 'large']);
export const BasicContentTitleLevelSchema = TitleLevelSchema;

function addBasicContentImageAltIssue(
  block: { layout?: string; imageUrl?: string; imageAlt?: string },
  ctx: z.RefinementCtx
): void {
  if (block.layout === 'no-image') {
    return;
  }

  if (block.imageUrl && (!block.imageAlt || block.imageAlt.trim().length < 1)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['imageAlt'],
      message: 'Alt text is required for accessibility',
    });
  }
}

function addSingleChoiceAnswerIssue(
  block: { gated: boolean; correctOptionId?: string; options?: unknown[]; questionDefinitionId?: number },
  ctx: z.RefinementCtx
): void {
  // Bank-referenced blocks persist only the reference; question content is
  // validated at the Question Bank and re-resolved server-side on read.
  if (block.questionDefinitionId !== undefined) {
    return;
  }
  if (!block.options || block.options.length < 2) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['options'],
      message: 'At least 2 options are required',
    });
  }
  if (block.gated && (!block.correctOptionId || block.correctOptionId.length === 0)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['correctOptionId'],
      message: 'Correct option is required when gated is enabled',
    });
  }
}

function addCategorizationIssue(
  block: { categories?: unknown[]; items?: unknown[]; questionDefinitionId?: number },
  ctx: z.RefinementCtx
): void {
  if (block.questionDefinitionId !== undefined) {
    return;
  }
  if (!block.categories || block.categories.length < 2) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['categories'],
      message: 'At least 2 categories are required',
    });
  }
  if (!block.items || block.items.length < 1) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['items'],
      message: 'At least 1 item is required',
    });
  }
}

function addClickExpandTopicLimitIssue(
  block: { expandType: ClickExpandType; topics: unknown[] },
  ctx: z.RefinementCtx
): void {
  const maxTopics = getClickExpandMaxTopics(block.expandType);
  if (block.topics.length <= maxTopics) {
    return;
  }

  ctx.addIssue({
    code: z.ZodIssueCode.custom,
    path: ['topics'],
    message: block.expandType === 'accordion'
      ? `Maximum ${maxTopics} topics allowed for accordion layout`
      : `Maximum ${maxTopics} topics allowed for top tabs layout`,
  });
}

// BasicContentBlockA schema - composite block with hero image and structured text
const BasicContentBlockASchemaBase = BaseBlockSchema.extend({
  type: z.literal('basic-content-a'),
  layout: BasicContentLayoutSchema.optional(),
  imageSize: BasicContentImageSizeSchema.optional(),
  imageUrl: z.string().url('Image URL must be a valid URL').optional(),
  imageAlt: z.string(),
  imageLibraryItemId: z.string().optional(),
  titleLevel: BasicContentTitleLevelSchema.optional(),
  title: z.string(),
  body: z.union([z.string(), TipTapContentSchema]),
  aspectRatio: z.string().regex(/^\d+:\d+$/, 'Aspect ratio must be in format "W:H" (e.g., "2:1")').default('2:1'),
});
export const BasicContentBlockASchema = BasicContentBlockASchemaBase.superRefine(addBasicContentImageAltIssue);

// BasicContentBlockB schema - two-column layout with text left, image right
const BasicContentBlockBSchemaBase = BaseBlockSchema.extend({
  type: z.literal('basic-content-b'),
  layout: BasicContentLayoutSchema.optional(),
  imageSize: BasicContentImageSizeSchema.optional(),
  imageUrl: z.string().url('Image URL must be a valid URL').optional(),
  imageAlt: z.string(),
  imageLibraryItemId: z.string().optional(),
  titleLevel: BasicContentTitleLevelSchema.optional(),
  title: z.string(),
  body: z.union([z.string(), TipTapContentSchema]),
  aspectRatio: z.string().regex(/^\d+:\d+$/, 'Aspect ratio must be in format "W:H" (e.g., "1:1")').default('1:1'),
});
export const BasicContentBlockBSchema = BasicContentBlockBSchemaBase.superRefine(addBasicContentImageAltIssue);

// StylizedTextCalloutBlock schema - gray callout card with restricted text formatting
export const StylizedTextCalloutBlockSchema = BaseBlockSchema.extend({
  type: z.literal('stylized-text-callout'),
  content: z.object({
    type: z.string(),
    content: z.array(z.any()).optional(),
  }).passthrough(),
  textSize: z.enum(['smaller', 'standard', 'larger']).default('standard'),
});

// NumberedListItem schema - for items in StylizedNumberedListBlock
const NumberedListItemSchema = z.object({
  id: z.string().min(1, 'Item ID is required'),
  content: z.object({
    type: z.string(),
    content: z.array(z.any()).optional(),
  }).passthrough(),
});

// ListStyle schema - for numbered or bulleted list presentation
export const ListStyleSchema = z.enum(['numbered', 'bulleted']);

// StylizedListTitleLevel schema - allowed list title heading levels
export const StylizedListTitleLevelSchema = TitleLevelSchema;

// SidebarCalloutTitleLevel schema - allowed main title levels
export const SidebarCalloutTitleLevelSchema = StylizedListTitleLevelSchema;

// StylizedNumberedListBlock schema - numbered list with dark circular badges
export const StylizedNumberedListBlockSchema = BaseBlockSchema.extend({
  type: z.literal('stylized-numbered-list'),
  title: z.string().optional(),
  titleLevel: StylizedListTitleLevelSchema.optional(),
  items: z.array(NumberedListItemSchema),
  listStyle: ListStyleSchema.optional(),
});

// StylizedSidebarCalloutBlock schema - two-column layout with main content and sidebar callout
export const StylizedSidebarCalloutBlockSchema = BaseBlockSchema.extend({
  type: z.literal('stylized-sidebar-callout'),
  title: TipTapContentSchema,
  titleLevel: SidebarCalloutTitleLevelSchema.optional(),
  body: TipTapContentSchema,
  calloutTitle: TipTapContentSchema,
  calloutBody: TipTapContentSchema,
});

// ComparisonSliderOption schema - option with collapsed label, title, body, optional image/video
const ComparisonSliderOptionSchema = z.object({
  collapsedLabel: z.string().optional(),
  title: TipTapContentSchema,
  body: TipTapContentSchema,
  imageUrl: z.string().url('Image URL must be a valid URL').optional(),
  imageAlt: z.string().min(5, 'Alt text must be at least 5 characters').optional(),
  imageLibraryItemId: z.string().optional(),
  videoUrl: z.string().url('Video URL must be a valid URL').optional(),
  videoPosterUrl: z.string().url('Poster URL must be a valid URL').optional(),
  videoPosterImageLibraryItemId: z.string().optional(),
  videoTitle: z.string().optional(),
  videoShortDescription: z.string().max(175, 'Short description must be 175 characters or fewer').optional(),
  centerstageVideoId: z.string().optional(),
}).refine(
  (data) => !data.imageUrl || (data.imageAlt && data.imageAlt.length >= 5),
  { message: 'Alt text is required when image URL is provided', path: ['imageAlt'] }
);

const ComparisonSliderContentTypeSchema = z.enum(['text-image', 'text-only', 'video']);
const ComparisonSliderTitleLevelSchema = TitleLevelSchema;

const ComparisonSliderImageSizeSchema = z.enum(['small', 'large']);

// ComparisonSliderBlock schema - two-option comparison with sliding transition
export const ComparisonSliderBlockSchema = BaseBlockSchema.extend({
  type: z.literal('comparison-slider'),
  contentType: ComparisonSliderContentTypeSchema,
  imageSize: ComparisonSliderImageSizeSchema.optional(),
  titleLevel: ComparisonSliderTitleLevelSchema.optional(),
  title: TipTapContentSchema,
  subtitle: TipTapContentSchema,
  option1: ComparisonSliderOptionSchema,
  option2: ComparisonSliderOptionSchema,
});

// ClickExpandTopic schema - individual expandable topic
const ClickExpandTopicSchema = z.object({
  id: z.string().min(1, 'Topic ID is required'),
  title: z.string().min(1, 'Topic title is required'),
  content: TipTapContentSchema,
  imageUrl: z.string().url('Image URL must be a valid URL').optional(),
  imageAlt: z.string().min(5, 'Alt text must be at least 5 characters').optional(),
  imageLibraryItemId: z.string().optional(),
}).refine(
  (data) => !data.imageUrl || (data.imageAlt && data.imageAlt.length >= 5),
  { message: 'Alt text is required when image URL is provided', path: ['imageAlt'] }
);

// ClickExpandBlock schema - expandable content with accordion or top-tabs layout
export const ClickExpandTitleLevelSchema = TitleLevelSchema;

const ClickExpandBlockSchemaBase = BaseBlockSchema.extend({
  type: z.literal('click-expand'),
  title: z.string(),
  titleLevel: ClickExpandTitleLevelSchema.optional(),
  description: TipTapContentSchema,
  expandType: z.enum(['accordion', 'top-tabs']),
  imageSize: z.enum(['large', 'small']).optional(),
  topics: z.array(ClickExpandTopicSchema).min(
    CLICK_EXPAND_MIN_TOPICS,
    `At least ${CLICK_EXPAND_MIN_TOPICS} topics are required`
  ),
});
export const ClickExpandBlockSchema = ClickExpandBlockSchemaBase.superRefine(addClickExpandTopicLimitIssue);

// SpectrumColorEntry schema - per-color title and rich text description
const SpectrumColorEntrySchema = z.object({
  title: z.string(),
  description: TipTapContentSchema,
});

const SpectrumColorSchema = z.enum(['green', 'yellow', 'orange', 'red']);

// WorkplaceSpectrumTitleLevel schema
export const WorkplaceSpectrumTitleLevelSchema = TitleLevelSchema;

// WorkplaceColorSpectrumBlock schema - interactive spectrum gauge with editable copy
export const WorkplaceColorSpectrumBlockSchema = BaseBlockSchema.extend({
  type: z.literal('workplace-color-spectrum'),
  questionDefinitionId: z.number().optional(),
  title: z.string().optional(),
  titleLevel: WorkplaceSpectrumTitleLevelSchema.optional(),
  // NA-384: ownership marker for the title. Absent means inheriting; content
  // predating NA-384 is migrated to true at rollout (frozen), not left absent.
  titleOverridden: z.boolean().optional(),
  // NA-384: resolution-supplied comparison value; read-only, stripped on save.
  titleBankValue: z.string().optional(),
  description: TipTapContentSchema.optional(),
  // NA-382: ownership marker for the question text. Absent means inheriting;
  // an empty value with the marker set is stripped on save and read as inheriting.
  descriptionOverridden: z.boolean().optional(),
  // NA-382: resolution-supplied comparison value; read-only, stripped on save.
  descriptionBankValue: TipTapContentSchema.optional(),
  colors: z.object({
    green: SpectrumColorEntrySchema,
    yellow: SpectrumColorEntrySchema,
    orange: SpectrumColorEntrySchema,
    red: SpectrumColorEntrySchema,
  }).optional(),
  correctAnswer: SpectrumColorSchema.optional(),
  correctAnswerExplanation: TipTapContentSchema.optional(),
  // NA-313: ownership marker for the explanation. Absent means inheriting.
  correctAnswerExplanationOverridden: z.boolean().optional(),
  // NA-313: resolution-supplied comparison value; read-only, stripped on save.
  correctAnswerExplanationBankValue: TipTapContentSchema.optional(),
});

const LikertScaleTypeSchema = z.enum([
  'agreement',
  'confidence',
  'importance',
  'likelihood',
  'frequency',
  'satisfaction',
]);

export const LikertScaleTitleLevelSchema = TitleLevelSchema;
const LikertScaleOptionCountSchema = z.union([z.literal(5), z.literal(7)]);

const LikertHealthyResponseSchema = z.enum(['none', 'left', 'right']);

export const LikertScaleBlockSchema = BaseBlockSchema.extend({
  type: z.literal('likert-scale'),
  questionDefinitionId: z.number().optional(),
  title: z.string().optional(),
  titleLevel: LikertScaleTitleLevelSchema.optional(),
  // NA-384: ownership marker for the title. Absent means inheriting; content
  // predating NA-384 is migrated to true at rollout (frozen), not left absent.
  titleOverridden: z.boolean().optional(),
  // NA-384: resolution-supplied comparison value; read-only, stripped on save.
  titleBankValue: z.string().optional(),
  description: TipTapContentSchema.optional(),
  // NA-382: ownership marker for the question text. Absent means inheriting;
  // an empty value with the marker set is stripped on save and read as inheriting.
  descriptionOverridden: z.boolean().optional(),
  // NA-382: resolution-supplied comparison value; read-only, stripped on save.
  descriptionBankValue: TipTapContentSchema.optional(),
  scale: LikertScaleTypeSchema.optional(),
  optionCount: LikertScaleOptionCountSchema.optional(),
  healthyResponse: LikertHealthyResponseSchema.optional(),
});

export const FreeformTextTitleLevelSchema = TitleLevelSchema;

export const FreeformTextBlockSchema = BaseBlockSchema.extend({
  type: z.literal('freeform-text'),
  questionDefinitionId: z.number().optional(),
  title: z.string().optional(),
  titleLevel: FreeformTextTitleLevelSchema.optional(),
  // Same NA-382/384-style ownership-marker contract as LikertScaleBlockSchema's.
  titleOverridden: z.boolean().optional(),
  titleBankValue: z.string().optional(),
  description: TipTapContentSchema.optional(),
  descriptionOverridden: z.boolean().optional(),
  descriptionBankValue: TipTapContentSchema.optional(),
});

// PolicyAcknowledgementBlock schema - policy document with acknowledgement flow
export const PolicyTypeSchema = z.enum(['pdf', 'link']);

export const PolicyAcknowledgementTitleLevelSchema = TitleLevelSchema;

export const PolicyAcknowledgementBlockSchema = BaseBlockSchema.extend({
  type: z.literal('policy-acknowledgement'),
  title: z.string(),
  titleLevel: PolicyAcknowledgementTitleLevelSchema.optional(),
  description: z.union([z.string(), TipTapContentSchema]),
  policyType: PolicyTypeSchema,
  policyUrl: z.string(),
  pdfHasLinks: z.boolean().optional(),
});

// LinkBlock schema - all fields optional (NA-409): blocks auto-save as authored.
export const LinkTitleLevelSchema = TitleLevelSchema;

export const LinkBlockSchema = BaseBlockSchema.extend({
  type: z.literal('link'),
  title: z.string().optional(),
  titleLevel: LinkTitleLevelSchema.optional(),
  description: z.union([z.string(), TipTapContentSchema]).optional(),
  linkText: z.string().optional(),
  linkUrl: z.string().optional(),
});

// ElectronicSignatureBlock schema - electronic signature with acknowledgement checkbox
export const ElectronicSignatureTitleLevelSchema = TitleLevelSchema;

export const ElectronicSignatureBlockSchema = BaseBlockSchema.extend({
  type: z.literal('electronic-signature'),
  title: z.string(),
  titleLevel: ElectronicSignatureTitleLevelSchema.optional(),
  description: z.union([z.string(), TipTapContentSchema]),
  checkboxLabel: z.string(),
});

export const MapTitleLevelSchema = TitleLevelSchema;

// Mirrors the MapRegion / MapVersion interfaces in maps.ts. Region
// descriptions are TipTap docs from the maps API but raw values (string,
// '' fallback) in published snapshots — hence the union.
export const MapRegionSchema = z.object({
  id: z.string(),
  mapVersionId: z.string(),
  regionKey: z.string(),
  name: z.string(),
  description: z.union([z.string(), TipTapContentSchema]),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const MapVersionSchema = z.object({
  id: z.string(),
  mapType: z.enum(['us', 'canada', 'emea', 'apac', 'latam']),
  title: z.string(),
  description: z.string(),
  interactionMessage: z.string(),
  regions: z.array(MapRegionSchema),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const MapBlockSchema = BaseBlockSchema.extend({
  type: z.literal('map'),
  mapVersionId: z.string().min(1, 'Map version is required'),
  title: z.string(),
  titleLevel: MapTitleLevelSchema.optional(),
  description: z.union([z.string(), TipTapContentSchema]),
  resolvedMapVersion: MapVersionSchema.optional(),
});

// CategorizationIcon schema
const CategorizationIconSchema = z.object({
  type: z.enum(['emoji', 'image']),
  value: z.string(),
  imageLibraryItemId: z.string().optional(),
});

// CategorizationItem schema
const CategorizationItemSchema = z.object({
  id: z.string().min(1, 'Item ID is required'),
  label: z.string(),
  icon: CategorizationIconSchema.optional(),
  correctCategoryId: z.string(),
});

// CategorizationCategory schema
const CategorizationCategorySchema = z.object({
  id: z.string().min(1, 'Category ID is required'),
  label: z.string(),
  icon: CategorizationIconSchema.optional(),
});

export const CategorizationTitleLevelSchema = TitleLevelSchema;

// CategorizationBlock schema - interactive categorization activity
const CategorizationBlockSchemaBase = BaseBlockSchema.extend({
  type: z.literal('categorization'),
  questionDefinitionId: z.number().optional(),
  title: z.string(),
  titleLevel: CategorizationTitleLevelSchema.optional(),
  // NA-384: ownership marker for the title. Absent means inheriting; content
  // predating NA-384 is migrated to true at rollout (frozen), not left absent.
  titleOverridden: z.boolean().optional(),
  // NA-384: resolution-supplied comparison value; read-only, stripped on save.
  titleBankValue: z.string().optional(),
  description: TipTapContentSchema.optional(),
  // NA-382: ownership marker for the question text. Absent means inheriting;
  // an empty value with the marker set is stripped on save and read as inheriting.
  descriptionOverridden: z.boolean().optional(),
  // NA-382: resolution-supplied comparison value; read-only, stripped on save.
  descriptionBankValue: TipTapContentSchema.optional(),
  instructionText: z.string().optional(),
  categories: z.array(CategorizationCategorySchema).max(3, 'Maximum 3 categories allowed').default([]),
  items: z.array(CategorizationItemSchema).default([]),
  correctFeedbackHeading: z.string().optional(),
  correctFeedbackBody: TipTapContentSchema.optional(),
  incorrectFeedbackHeading: z.string().optional(),
  incorrectFeedbackBody: TipTapContentSchema.optional(),
  submitButtonLabel: z.string().optional(),
});
export const CategorizationBlockSchema = CategorizationBlockSchemaBase.superRefine(addCategorizationIssue);

// SingleChoiceOption schema
const SingleChoiceOptionSchema = z.object({
  id: z.string().min(1, 'Option ID is required'),
  label: z.string(),
});

export const SingleChoiceAnswerTitleLevelSchema = TitleLevelSchema;

// SingleChoiceAnswerBlock schema - interactive single-choice answer with drag-and-drop
const SingleChoiceAnswerBlockSchemaBase = BaseBlockSchema.extend({
  type: z.literal('single-choice-answer'),
  questionDefinitionId: z.number().optional(),
  title: z.string(),
  titleLevel: SingleChoiceAnswerTitleLevelSchema.optional(),
  // NA-384: ownership marker for the title. Absent means inheriting; content
  // predating NA-384 is migrated to true at rollout (frozen), not left absent.
  titleOverridden: z.boolean().optional(),
  // NA-384: resolution-supplied comparison value; read-only, stripped on save.
  titleBankValue: z.string().optional(),
  description: TipTapContentSchema.optional(),
  // NA-382: ownership marker for the question text. Absent means inheriting;
  // an empty value with the marker set is stripped on save and read as inheriting.
  descriptionOverridden: z.boolean().optional(),
  // NA-382: resolution-supplied comparison value; read-only, stripped on save.
  descriptionBankValue: TipTapContentSchema.optional(),
  options: z.array(SingleChoiceOptionSchema).default([]),
  gated: z.boolean(),
  correctOptionId: z.string().optional(),
  correctExplanation: TipTapContentSchema.optional(),
  // NA-313: ownership marker for the explanation. Absent means inheriting.
  correctExplanationOverridden: z.boolean().optional(),
  // NA-313: resolution-supplied comparison value; read-only, stripped on save.
  correctExplanationBankValue: TipTapContentSchema.optional(),
  healthyOptionId: z.string().optional(),
});
export const SingleChoiceAnswerBlockSchema = SingleChoiceAnswerBlockSchemaBase.superRefine(addSingleChoiceAnswerIssue);

// LessonCompleteBlock schema - end-of-lesson marker with celebration
const LessonCompleteTitleLevelSchema = TitleLevelSchema;

export const LessonCompleteBlockSchema = BaseBlockSchema.extend({
  type: z.literal('lesson-complete'),
  imageUrl: z.string().url('Image URL must be a valid URL').optional(),
  imageAlt: z.string().min(5, 'Alt text must be at least 5 characters').optional(),
  imageLibraryItemId: z.string().optional(),
  title: z.string(),
  titleLevel: LessonCompleteTitleLevelSchema.optional(),
  description: TipTapContentSchema,
  successAnimationType: SuccessAnimationTypeSchema.optional(),
});

// Discriminated union schema for CourseBlock
const CourseBlockDiscriminatedSchema = z.discriminatedUnion('type', [
  VideoBlockSchema,
  BasicContentBlockASchemaBase,
  BasicContentBlockBSchemaBase,
  StylizedTextCalloutBlockSchema,
  StylizedNumberedListBlockSchema,
  StylizedSidebarCalloutBlockSchema,
  ComparisonSliderBlockSchema,
  ClickExpandBlockSchemaBase,
  WorkplaceColorSpectrumBlockSchema,
  LikertScaleBlockSchema,
  PolicyAcknowledgementBlockSchema,
  ElectronicSignatureBlockSchema,
  MapBlockSchema,
  CategorizationBlockSchemaBase,
  SingleChoiceAnswerBlockSchemaBase,
  FreeformTextBlockSchema,
  LessonCompleteBlockSchema,
  LinkBlockSchema,
]);

export const CourseBlockSchema = CourseBlockDiscriminatedSchema.superRefine((block, ctx) => {
  if (block.type === 'basic-content-a' || block.type === 'basic-content-b') {
    addBasicContentImageAltIssue(block, ctx);
    return;
  }

  if (block.type === 'single-choice-answer') {
    addSingleChoiceAnswerIssue(block, ctx);
    return;
  }

  if (block.type === 'categorization') {
    addCategorizationIssue(block, ctx);
    return;
  }

  if (block.type === 'click-expand') {
    addClickExpandTopicLimitIssue(block, ctx);
  }
});

// Type exports inferred from schemas
export type VideoTrackInput = z.infer<typeof VideoTrackSchema>;
export type VideoBlockInput = z.infer<typeof VideoBlockSchema>;
export type BasicContentBlockAInput = z.infer<typeof BasicContentBlockASchema>;
export type BasicContentBlockBInput = z.infer<typeof BasicContentBlockBSchema>;
export type StylizedTextCalloutBlockInput = z.infer<typeof StylizedTextCalloutBlockSchema>;
export type StylizedNumberedListBlockInput = z.infer<typeof StylizedNumberedListBlockSchema>;
export type StylizedSidebarCalloutBlockInput = z.infer<typeof StylizedSidebarCalloutBlockSchema>;
export type ComparisonSliderBlockInput = z.infer<typeof ComparisonSliderBlockSchema>;
export type ComparisonSliderOptionInput = z.infer<typeof ComparisonSliderOptionSchema>;
export type ClickExpandTopicInput = z.infer<typeof ClickExpandTopicSchema>;
export type ClickExpandBlockInput = z.infer<typeof ClickExpandBlockSchema>;
export type WorkplaceColorSpectrumBlockInput = z.infer<typeof WorkplaceColorSpectrumBlockSchema>;
export type LikertScaleBlockInput = z.infer<typeof LikertScaleBlockSchema>;
export type PolicyAcknowledgementBlockInput = z.infer<typeof PolicyAcknowledgementBlockSchema>;
export type ElectronicSignatureBlockInput = z.infer<typeof ElectronicSignatureBlockSchema>;
export type MapBlockInput = z.infer<typeof MapBlockSchema>;
export type CategorizationBlockInput = z.infer<typeof CategorizationBlockSchema>;
export type SingleChoiceAnswerBlockInput = z.infer<typeof SingleChoiceAnswerBlockSchema>;
export type LessonCompleteBlockInput = z.infer<typeof LessonCompleteBlockSchema>;
export type LinkBlockInput = z.infer<typeof LinkBlockSchema>;
export type CourseBlockInput = z.infer<typeof CourseBlockSchema>;

// Validation result type
export interface ValidationError {
  field: string;
  message: string;
  code: 'required' | 'invalid_type' | 'too_short' | 'invalid_url' | 'missing_caption';
}

export type ValidationResult<T> =
  | { success: true; data: T }
  | { success: false; errors: ValidationError[] };

// Helper function to map Zod errors to ValidationError format
function mapZodError(error: z.ZodError): ValidationError[] {
  return error.errors.map((err) => {
    let code: ValidationError['code'] = 'invalid_type';

    if (err.code === 'too_small') {
      code = err.minimum === 1 ? 'required' : 'too_short';
    } else if (err.code === 'invalid_string' && err.validation === 'url') {
      code = 'invalid_url';
    } else if (err.message.includes('caption')) {
      code = 'missing_caption';
    }

    return {
      field: err.path.join('.'),
      message: err.message,
      code,
    };
  });
}

function validateWithSchema<Schema extends z.ZodTypeAny>(
  schema: Schema,
  data: unknown,
  getExtraErrors?: (parsed: z.output<Schema>) => ValidationError[]
): ValidationResult<z.output<Schema>> {
  const result = schema.safeParse(data);
  if (!result.success) {
    return { success: false, errors: mapZodError(result.error) };
  }

  const extraErrors = getExtraErrors?.(result.data) ?? [];
  if (extraErrors.length > 0) {
    return { success: false, errors: extraErrors };
  }

  return { success: true, data: result.data };
}

// Validation functions
export function validateVideoBlock(data: unknown): ValidationResult<VideoBlockInput> {
  return validateWithSchema(VideoBlockSchema, data);
}

export function validateCourseBlock(data: unknown): ValidationResult<CourseBlockInput> {
  return validateWithSchema(CourseBlockSchema, data);
}

export function validateBasicContentBlockA(data: unknown): ValidationResult<BasicContentBlockAInput> {
  return validateWithSchema(BasicContentBlockASchema, data);
}

export function validateBasicContentBlockB(data: unknown): ValidationResult<BasicContentBlockBInput> {
  return validateWithSchema(BasicContentBlockBSchema, data);
}

export function validateStylizedTextCalloutBlock(data: unknown): ValidationResult<StylizedTextCalloutBlockInput> {
  return validateWithSchema(StylizedTextCalloutBlockSchema, data);
}

export function validateStylizedNumberedListBlock(data: unknown): ValidationResult<StylizedNumberedListBlockInput> {
  return validateWithSchema(StylizedNumberedListBlockSchema, data);
}

export function validateStylizedSidebarCalloutBlock(data: unknown): ValidationResult<StylizedSidebarCalloutBlockInput> {
  return validateWithSchema(StylizedSidebarCalloutBlockSchema, data);
}

export function validateComparisonSliderBlock(data: unknown): ValidationResult<ComparisonSliderBlockInput> {
  return validateWithSchema(ComparisonSliderBlockSchema, data);
}

export function validateClickExpandBlock(data: unknown): ValidationResult<ClickExpandBlockInput> {
  return validateWithSchema(ClickExpandBlockSchema, data);
}

export function validateWorkplaceColorSpectrumBlock(data: unknown): ValidationResult<WorkplaceColorSpectrumBlockInput> {
  return validateWithSchema(WorkplaceColorSpectrumBlockSchema, data);
}

export function validateLikertScaleBlock(data: unknown): ValidationResult<LikertScaleBlockInput> {
  return validateWithSchema(LikertScaleBlockSchema, data);
}

export function validatePolicyAcknowledgementBlock(data: unknown): ValidationResult<PolicyAcknowledgementBlockInput> {
  return validateWithSchema(PolicyAcknowledgementBlockSchema, data);
}

export function validateElectronicSignatureBlock(data: unknown): ValidationResult<ElectronicSignatureBlockInput> {
  return validateWithSchema(ElectronicSignatureBlockSchema, data);
}

export function validateMapBlock(data: unknown): ValidationResult<MapBlockInput> {
  return validateWithSchema(MapBlockSchema, data);
}

export function validateCategorizationBlock(data: unknown): ValidationResult<CategorizationBlockInput> {
  return validateWithSchema(CategorizationBlockSchema, data);
}

export function validateSingleChoiceAnswerBlock(data: unknown): ValidationResult<SingleChoiceAnswerBlockInput> {
  return validateWithSchema(SingleChoiceAnswerBlockSchema, data);
}

export function validateLinkBlock(data: unknown): ValidationResult<LinkBlockInput> {
  return validateWithSchema(LinkBlockSchema, data);
}

export function validateLessonCompleteBlock(data: unknown): ValidationResult<LessonCompleteBlockInput> {
  return validateWithSchema(LessonCompleteBlockSchema, data);
}
